import type { Observable } from 'rxjs';
import httpService from './httpService';
import type { TripLeg, TripLegPackage } from '../data/models/trip-leg.model';
import type { Driver } from '../data/models/driver.model';
import type { PagedList } from '../data/models/paged-list.model';
import type { SortModel, TripSortModel } from '../data/models/sort-model.model';
import type { TripLegStatusBC } from '../data/models/trip-leg-status.model';
import { TripLegSearchRequest } from '../data/requests/trip-legs';
import { TripLegSearchResponseItem } from '../data/models/trip-leg-search.model';

export type TripLegOptimizedParams = {
  page: number;
  pageSize: number;
  sortModel?: SortModel;
  filter?: string;
  search?: string;
  isDriverAssigned?: boolean;
  unattestedOnly?: boolean;
  includeTripLegStatusLog?: boolean;
  isPasswordPermanent?: boolean;
  tripLegStatusBCs?: TripLegStatusBC[];
  pickupStartDate?: string;
  pickupEndDate?: string;
  transportationProviderID?: number;
};

class TripService {
  private readonly apiBase;
  private readonly apiDriverBase;

  constructor() {
    const { __environment__ } = window as unknown as {
      __environment__: {
        REACT_APP_TRIP_API_URL: string;
        REACT_APP_DRIVER_API_URL: string;
      };
    };
    this.apiBase = __environment__.REACT_APP_TRIP_API_URL.trim();
    this.apiDriverBase = __environment__.REACT_APP_DRIVER_API_URL.trim();
  }

  private getTripLegIdentifier(
    referenceID?: string,
    tripLegID?: number,
  ): string {
    if (!referenceID && !tripLegID) {
      throw new Error(
        'Either referenceID or tripLegID is required when requesting a trip leg',
      );
    }
    return referenceID ? `REF-${referenceID}` : `ID-${tripLegID}`;
  }

  public findAllocatedTrips(
    providerId: number,
    pageSize: number,
    page: number,
    sortModel: TripSortModel | null,
    assigned: boolean,
    unassigned: boolean,
    state?: string,
  ): Observable<PagedList<TripLeg>> {
    const stateParam = `state=${state ? state : 'active'}`;
    let url = `${this.apiBase}?transportationProviderID=${providerId}&page=${page}&pageSize=${pageSize}&${stateParam}`;
    if (sortModel) {
      url += `&sort=${sortModel.column}&sortOrder=${sortModel.direction}`;
    }
    if (assigned && !unassigned) {
      url += `&isDriverAssigned=true`;
    } else if (unassigned && !assigned) {
      url += `&isDriverAssigned=false`;
    }
    return httpService.getJSON(url);
  }

  // public getTrip(tripLegID: string): Observable<TripLeg> {
  //   const id = userid || 'me';
  //   const url = `${this.apiBase}/${id}`;
  //   return httpService.getJSON(url);
  // }

  // This is the old version but it doesn't work so we're going with the below version for now
  // public findTripEligibleDrivers(
  //   providerId: number,
  //   tripLegID: string,
  //   pageSize: number,
  //   page: number,
  //   sortModel: SortModel | null,
  //   isDriverAssigned?: boolean,
  // ): Observable<PagedList<Driver>> {
  //   let url = `${this.apiBase}/${tripLegID}/candidate-drivers?page=${page}&pageSize=${pageSize}&transportationProviderID=${providerId}`;
  //   if (sortModel) {
  //     url += `&sort=${sortModel.column}&sortOrder=${sortModel.direction}`;
  //   }
  //   if (typeof isDriverAssigned === 'boolean') {
  //     url += `&isDriverAssigned=${isDriverAssigned}`;
  //   }
  //   return httpService.getJSON(url);
  // }

  public findTripEligibleDrivers(
    providerId: number,
    pageSize: number,
    page: number,
    filter: string,
    search: string,
    sortModel: SortModel | null,
  ): Observable<PagedList<Driver>> {
    const f = encodeURIComponent(filter);
    const s = encodeURIComponent(search);

    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    const isEmailValid = emailRegex.test(search);

    const searchFlag = isEmailValid
      ? `&email=${encodeURIComponent(search)}`
      : `&search=${s}`;
    let url = `${this.apiDriverBase}/optimized?page=${page}&pageSize=${pageSize}&filter=${f}${searchFlag}&isPasswordPermanent=true`;
    if (sortModel) {
      url += `&sort=${sortModel.column}&sortOrder=${sortModel.direction}`;
    }
    if (providerId) {
      url += `&provider=${providerId}`;
    }
    return httpService.getJSON(url);
  }

  public assignDriverToTrip({
    driverID,
    tripLegReferenceID,
    tripLegID,
    updateOnly,
  }: {
    driverID: number;
    tripLegReferenceID?: string;
    tripLegID?: number;
    updateOnly?: boolean;
  }): Observable<TripLeg> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBase}/${identifier}/assign-driver`;
    return httpService.postJSON(url, {
      driverID,
      tripLegReferenceID,
      updateOnly,
    });
  }

  public assignVehicleToTrip({
    vehicleID,
    tripLegReferenceID,
    tripLegID,
    updateOnly,
  }: {
    vehicleID: number;
    tripLegReferenceID?: string;
    tripLegID?: number;
    updateOnly?: boolean;
  }): Observable<TripLeg> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBase}/${identifier}/assign-vehicle`;
    return httpService.postJSON(url, {
      vehicleID,
      tripLegReferenceID,
      updateOnly,
    });
  }

  public getTripLegByID({
    tripLegReferenceID,
    tripLegID,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
  }): Observable<TripLegPackage> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBase}/${identifier}`;
    return httpService.getJSON(url);
  }

  public getTripLegsSearch(
    request: TripLegSearchRequest,
  ): Observable<PagedList<TripLegSearchResponseItem>> {
    const queryParams = httpService.getQueryParams(request);
    const url = `${this.apiBase}/search?${queryParams}`;
    return httpService.getJSON(url);
  }

  public setTripLegUnderDiscussion({
    tripLegReferenceID,
    tripLegID,
    discussionReason,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
    discussionReason: string;
  }): Observable<TripLeg> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBase}/${identifier}/under-discussion`;
    return httpService.postJSON(url, { tripLegID, discussionReason });
  }

  public clearTripLegUnderDiscussion({
    tripLegReferenceID,
    tripLegID,
    discussionDecisionOutcome,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
    discussionDecisionOutcome: string;
  }): Observable<TripLeg> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBase}/${identifier}/clear-under-discussion`;
    return httpService.postJSON(url, {
      tripLegID,
      discussionDecisionOutcome,
    });
  }

  public setException({
    tripLegReferenceID,
    tripLegID,
    tripLegExceptionID,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
    tripLegExceptionID: number;
  }): Observable<TripLegPackage> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBase}/${identifier}/exception`;
    return httpService.postJSON(url, { tripLegID, tripLegExceptionID });
  }

  public clearException({
    tripLegReferenceID,
    tripLegID,
  }: {
    tripLegReferenceID?: string;
    tripLegID?: number;
  }): Observable<TripLegPackage> {
    const identifier = this.getTripLegIdentifier(tripLegReferenceID, tripLegID);
    const url = `${this.apiBase}/${identifier}/clear-exception`;
    return httpService.postJSON(url, { tripLegID });
  }
}

const tripService = new TripService();

export default tripService;
